import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Trash2, Filter, MessageSquareText, Clock, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem('dotcall_admin_token');
const authHeader = () => ({ headers: { Authorization: `Bearer ${token()}` } });

const STATUS_META = {
  new: { label: 'New', className: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  contacted: { label: 'Contacted', className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  qualified: { label: 'Qualified', className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  lost: { label: 'Lost', className: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

export default function AdminLeadsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/chatbot-leads`, authHeader());
      setItems(res.data);
    } catch { toast.error('Failed to load leads'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/api/admin/chatbot-leads/${id}`, { status }, authHeader());
      setItems(prev => prev.map(i => i.id === id ? { ...i, status } : i));
    } catch { toast.error('Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await axios.delete(`${API}/api/admin/chatbot-leads/${id}`, authHeader());
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success('Lead deleted');
    } catch { toast.error('Delete failed'); }
  };

  const filtered = items.filter(i => {
    if (filter !== 'all' && i.status !== filter) return false;
    if (search && !`${i.name} ${i.phone} ${i.interest || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = {
    all: items.length,
    new: items.filter(i => i.status === 'new').length,
    contacted: items.filter(i => i.status === 'contacted').length,
    qualified: items.filter(i => i.status === 'qualified').length,
    lost: items.filter(i => i.status === 'lost').length,
  };

  return (
    <div className="p-6 space-y-5" data-testid="admin-leads-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Chatbot Leads</h1>
          <p className="text-sm text-white/50 mt-1">Names &amp; phone numbers captured by the DotCall AI chatbot</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <select value={filter} onChange={e => setFilter(e.target.value)} className="pl-9 pr-3 h-10 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm focus:outline-none focus:border-amber-500/40" data-testid="leads-filter">
              <option value="all" className="bg-[#0B1221]">All ({counts.all})</option>
              <option value="new" className="bg-[#0B1221]">New ({counts.new})</option>
              <option value="contacted" className="bg-[#0B1221]">Contacted ({counts.contacted})</option>
              <option value="qualified" className="bg-[#0B1221]">Qualified ({counts.qualified})</option>
              <option value="lost" className="bg-[#0B1221]">Lost ({counts.lost})</option>
            </select>
          </div>
          <Input placeholder="Search name or phone..." value={search} onChange={e => setSearch(e.target.value)} className="bg-white/[0.03] border-white/10 text-white w-64" data-testid="leads-search" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Total Leads', value: counts.all },
          { label: 'New', value: counts.new },
          { label: 'Contacted', value: counts.contacted },
          { label: 'Qualified', value: counts.qualified },
          { label: 'Lost', value: counts.lost },
        ].map(s => (
          <div key={s.label} className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
            <div className="text-xs text-white/50 uppercase tracking-wider mb-1">{s.label}</div>
            <div className="text-2xl font-bold text-white">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-white/40">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-white/40">
            <MessageSquareText className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No leads yet</p>
            <p className="text-xs mt-1">Leads will appear here when the chatbot collects name + phone from visitors.</p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="leads-table">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-white/50 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Interest</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Received</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {filtered.map(lead => (
                  <tr key={lead.id} className="hover:bg-white/[0.02] transition-colors" data-testid={`lead-row-${lead.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-amber-600/15 flex items-center justify-center"><User className="w-3.5 h-3.5 text-amber-300" /></div>
                        <span className="text-white font-medium">{lead.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <a href={`tel:${lead.phone}`} className="text-amber-300 hover:text-amber-200 flex items-center gap-1"><Phone className="w-3 h-3" /> {lead.phone}</a>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-white/60 max-w-[280px] truncate" title={lead.interest}>{lead.interest || '—'}</td>
                    <td className="px-4 py-3">
                      <select value={lead.status} onChange={e => setStatus(lead.id, e.target.value)} className={`text-xs px-2 py-1 rounded border ${STATUS_META[lead.status]?.className || 'bg-white/5 text-white border-white/10'} focus:outline-none cursor-pointer`}>
                        {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k} className="bg-[#0B1221]">{v.label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-white/50 text-xs"><span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(lead.created_at).toLocaleString()}</span></td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => remove(lead.id)} className="border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300" data-testid={`lead-delete-${lead.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
